import React, { useEffect, useState } from "react";
import { X as CloseIcon } from "lucide-react";
import useIsMobile from "@/hooks/useIsMobile";
import useShortcuts, { getAllShortcuts, subscribeShortcuts } from "@/hooks/useShortcuts";
import { cn } from "@/lib/utils";

/**
 * GLOBAL shortcuts — always available anywhere in the app.
 * Page-specific shortcuts register/unregister via `useShortcuts` in that page.
 */
const GLOBAL_SHORTCUTS_DESCRIPTOR = [
  { keys: "?", description: "Show keyboard shortcuts", group: "Global" },
  { keys: "/", description: "Focus search", group: "Global" },
  { keys: "mod+k", description: "Command palette / search", group: "Global" },
  { keys: "mod+shift+k", description: "Brain dump (quick capture)", group: "Global" },
  { keys: "escape", description: "Close modal / cancel edit", group: "Global" },
];

const NAV_DESCRIPTORS = [
  { keys: "j", description: "Next row", group: "Navigation" },
  { keys: "k", description: "Previous row", group: "Navigation" },
  { keys: "enter", description: "Open selected", group: "Navigation" },
  { keys: "e", description: "Edit selected", group: "Navigation" },
];

const ROW_ACTIONS = [
  { keys: "c", description: "Complete task", group: "Row actions" },
  { keys: "o / i / r / p / d", description: "Status: Open · In-progress · Review · Paused · Done", group: "Row actions" },
  { keys: "h / m / l / u", description: "Priority: High · Medium · Low · Urgent", group: "Row actions" },
  { keys: "mod+z", description: "Undo last action", group: "Row actions" },
];

/**
 * ShortcutsHelp — Overlay showing all registered shortcuts.
 * Opens on "?" keypress. Hidden by default. Auto-disabled on touch-only devices.
 *
 * Mount once at App.js level:
 *   <ShortcutsHelp />
 */
export function ShortcutsHelp() {
  const [open, setOpen] = useState(false);
  const [registered, setRegistered] = useState([]);
  const isMobile = useIsMobile();

  // Subscribe to the registry so the list stays live as pages mount/unmount
  useEffect(() => {
    setRegistered(getAllShortcuts());
    const unsub = subscribeShortcuts((list) => setRegistered(list));
    return unsub;
  }, []);

  // Bind "?" to toggle, Escape to close
  useShortcuts([
    { keys: "?", handler: () => setOpen((v) => !v), description: "Show keyboard shortcuts" },
    { keys: "escape", handler: () => setOpen(false), description: "Close overlay" },
  ], { enabled: true });

  // Don't render on touch-only devices (no keyboard)
  if (isMobile) return null;
  if (!open) return null;

  const all = [
    ...GLOBAL_SHORTCUTS_DESCRIPTOR,
    ...NAV_DESCRIPTORS,
    ...ROW_ACTIONS,
    // Add currently-registered page shortcuts with descriptions
    ...registered
      .filter((s) => s.description)
      .map((s) => ({ ...s, group: s.group || "This page" })),
  ];

  // De-duplicate by `keys`
  const seen = new Set();
  const deduped = [];
  for (const s of all) {
    const k = `${s.group}|${s.keys}`;
    if (seen.has(k)) continue;
    seen.add(k);
    deduped.push(s);
  }

  const grouped = deduped.reduce((acc, s) => {
    (acc[s.group] = acc[s.group] || []).push(s);
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "w-full max-w-xl max-h-[80vh] overflow-y-auto",
          "bg-[var(--s1)] border border-[var(--bdr)] rounded-2xl shadow-2xl"
        )}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bdr)]">
          <h2 className="font-heading text-lg text-[var(--t1)]">Keyboard shortcuts</h2>
          <button
            onClick={() => setOpen(false)}
            className="p-1.5 rounded hover:bg-[var(--s2)] text-[var(--t3)]"
            aria-label="Close"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-5">
          {Object.entries(grouped).map(([group, items]) => (
            <section key={group}>
              <h3 className="text-xs uppercase tracking-wider text-[var(--t3)] font-semibold mb-2">
                {group}
              </h3>
              <div className="space-y-1">
                {items.map((s, i) => (
                  <div
                    key={`${group}-${i}`}
                    className="flex items-center justify-between py-1.5"
                  >
                    <span className="text-sm text-[var(--t2)]">{s.description}</span>
                    <KbdKeys keys={s.keys} />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-[var(--bdr)] text-xs text-[var(--t3)]">
          Press <Kbd>?</Kbd> anywhere to toggle this list
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }) {
  return (
    <span className="inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 text-[11px] font-mono rounded border border-[var(--bdr)] bg-[var(--s0,#0a0a0a)] text-[var(--t2)]">
      {children}
    </span>
  );
}

function KbdKeys({ keys }) {
  // Render each key-combo nicely: "mod+k" → ⌘ K,  "shift+c" → ⇧ C
  const combos = keys.split(/\s*\/\s*/); // "o / i / r" → ["o","i","r"]
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  return (
    <span className="flex items-center gap-1">
      {combos.map((combo, i) => {
        const parts = combo.split("+").map((p) => p.trim());
        return (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-[var(--t3)] text-xs">or</span>}
            {parts.map((p, j) => {
              const display = p === "mod" ? (isMac ? "⌘" : "Ctrl")
                : p === "shift" ? "⇧"
                : p === "alt" ? (isMac ? "⌥" : "Alt")
                : p === "enter" ? "↵"
                : p === "escape" ? "Esc"
                : p === "arrowup" ? "↑"
                : p === "arrowdown" ? "↓"
                : p.length === 1 ? p.toUpperCase() : p;
              return (
                <React.Fragment key={j}>
                  {j > 0 && <span className="text-[var(--t3)] text-xs">+</span>}
                  <Kbd>{display}</Kbd>
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </span>
  );
}

export default ShortcutsHelp;
